import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useMenuPermissions,
  useUpdateMenuPermission,
  type MenuKey,
} from "@/hooks/use-menu-permissions";
import { SYSTEM_ROLE_OPTIONS, SYSTEM_ROLE_LABELS } from "@/lib/constants";
import { SrIcon } from "@/components/ui/SrIcon";
import { MENU_HIERARCHY } from "@/lib/menu-registry";
import { useGridNavigation } from "@/hooks/use-grid-navigation";
import { toast } from "sonner";
import { showUndoToast } from "@/lib/undo-toast";

// VIB-1179: Hierarkisk meny-struktur avledes nå fra det sentrale menyregisteret
// (src/lib/menu-registry.ts). Parent rendres som hovedrad, subs som innrykkede
// rader. Hvis parent er false for en rolle, vises subs som disabled (UI
// cascading) — DB beholder selvstendig verdi for hver row.

// VIB-2545: flat radliste (parent + subs) så matrisen kan navigeres som et grid.
const GRID_ROWS = MENU_HIERARCHY.flatMap((item) => [
  { key: item.key, parentKey: null as MenuKey | null },
  ...(item.subs ?? []).map((sub) => ({ key: sub.key, parentKey: item.key as MenuKey | null })),
]);

export function SystemrollerCard() {
  const { data: permissions, isLoading } = useMenuPermissions();
  const update = useUpdateMenuPermission();

  const canAccess = (role: string, menuKey: MenuKey): boolean =>
    permissions?.find((p) => p.role === role && p.menu_key === menuKey)
      ?.can_access ?? false;

  const isCellDisabled = (role: string, menuKey: MenuKey, parentKey: MenuKey | null) =>
    role === "superadmin" || (parentKey !== null && !canAccess(role, parentKey));

  // VIB-2545: roving tabindex — kun én celle i Tab-rekkefølgen, piltaster flytter.
  const grid = useGridNavigation({
    rows: GRID_ROWS.length,
    cols: SYSTEM_ROLE_OPTIONS.length,
    isEnabled: (r, c) => {
      const row = GRID_ROWS[r];
      return !!row && !isCellDisabled(SYSTEM_ROLE_OPTIONS[c], row.key, row.parentKey);
    },
  });
  const rowIndexOf = (key: MenuKey) => GRID_ROWS.findIndex((r) => r.key === key);

  if (isLoading) return null;

  const onError = (err: unknown) => toast.error(`Feil: ${(err as Error).message}`);

  // VIB-2530: ett klikk skriver rett til DB — angre-toast i stedet for dialog.
  // Angre er en ny upsert med forrige verdi (egen rad i audit-loggen).
  const handleToggle = (role: string, menuKey: MenuKey, current: boolean) => {
    const label = `${SYSTEM_ROLE_LABELS[role]} -> ${menuKey}`;
    update.mutate(
      { role, menu_key: menuKey, can_access: !current },
      {
        onSuccess: () =>
          showUndoToast(`${label}: lagret`, () =>
            update.mutate(
              { role, menu_key: menuKey, can_access: current },
              { onSuccess: () => toast.success(`${label}: angret`), onError },
            ),
          ),
        onError,
      },
    );
  };

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <SrIcon name="shield" className="h-4 w-4 text-muted-foreground" />
          <h2 className="font-display text-base">
            Systemroller (RBAC) — meny-tilgang
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" role="grid" aria-label="Meny-tilgang per systemrolle">
            <thead>
              <tr>
                <th className="text-left p-2 font-medium">Meny-side</th>
                {SYSTEM_ROLE_OPTIONS.map((role) => (
                  <th key={role} className="text-center p-2 font-medium">
                    {SYSTEM_ROLE_LABELS[role]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MENU_HIERARCHY.flatMap((item) => {
                const parentRow = (
                  <tr key={item.key} className="border-t border-border/50">
                    <td className="p-2 font-medium">{item.label}</td>
                    {SYSTEM_ROLE_OPTIONS.map((role, colIdx) => {
                      const current = canAccess(role, item.key);
                      return (
                        <td key={role} className="text-center p-2" role="gridcell">
                          <Checkbox
                            checked={current}
                            onCheckedChange={() =>
                              handleToggle(role, item.key, current)
                            }
                            disabled={isCellDisabled(role, item.key, null)}
                            aria-label={`${SYSTEM_ROLE_LABELS[role]} ${item.label}`}
                            {...grid.getCellProps(rowIndexOf(item.key), colIdx)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
                const subRows = (item.subs ?? []).map((sub) => (
                  <tr key={sub.key} className="border-t border-border/30 bg-muted/20">
                    <td className="p-2 pl-8 text-muted-foreground">
                      ↳ {sub.label}
                    </td>
                    {SYSTEM_ROLE_OPTIONS.map((role, colIdx) => {
                      const current = canAccess(role, sub.key);
                      return (
                        <td key={role} className="text-center p-2" role="gridcell">
                          <Checkbox
                            checked={current}
                            onCheckedChange={() =>
                              handleToggle(role, sub.key, current)
                            }
                            disabled={isCellDisabled(role, sub.key, item.key)}
                            aria-label={`${SYSTEM_ROLE_LABELS[role]} ${sub.label}`}
                            {...grid.getCellProps(rowIndexOf(sub.key), colIdx)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ));
                return [parentRow, ...subRows];
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
